import type {
  RdoApontamento,
  RdoAtividade,
  RdoAtividadePhase,
} from '@/modules/field/types/rdo-form';
import { calculateParentCompletion } from '@/modules/field/utils/scheduleParentCompletion';

const APONTAR_KEY_BY_TASK: Record<string, keyof RdoAtividade['apontar']> = {
  cabling: 'cabo',
  installation: 'instal',
  configuration: 'confg',
};

export function emptyApontar(): RdoAtividade['apontar'] {
  return { cabo: 0, instal: 0, confg: 0 };
}

export function hydrateApontarFromApontamentos(
  apontamentos: RdoApontamento[],
): RdoAtividade['apontar'] {
  const apontar = emptyApontar();

  for (const apontamento of apontamentos) {
    const key = APONTAR_KEY_BY_TASK[apontamento.taskType];
    if (key) {
      apontar[key] += Number(apontamento.quantity) || 0;
    }
  }

  return apontar;
}

function mergeApontar(
  fromApontamentos: RdoAtividade['apontar'],
  fromRaw: Partial<RdoAtividade['apontar']> | undefined,
): RdoAtividade['apontar'] {
  return {
    cabo: Math.max(fromApontamentos.cabo, Number(fromRaw?.cabo) || 0),
    instal: Math.max(fromApontamentos.instal, Number(fromRaw?.instal) || 0),
    confg: Math.max(fromApontamentos.confg, Number(fromRaw?.confg) || 0),
  };
}

export function buildApontamentosFromAtividade(act: RdoAtividade): RdoApontamento[] {
  if (act.isPendingType) {
    const pct = act.completionPercentage;
    if (pct == null || !act.scheduleItemId) {
      return [];
    }

    const safePct = Math.min(100, Math.max(0, Number(pct)));
    if (safePct <= 0) {
      return [];
    }

    return [
      {
        scheduleItemId: act.scheduleItemId,
        taskType: act.activityType ?? 'activity',
        label: act.title,
        quantity: 1,
        actualQuantity: 0,
        totalQuantity: 0,
        completionPercentage: safePct,
      },
    ];
  }

  const rows: RdoApontamento[] = [];

  if (act.phases?.length) {
    for (const phase of act.phases) {
      const key = APONTAR_KEY_BY_TASK[phase.taskType];
      const quantity = key ? Number(act.apontar[key] ?? 0) : 0;

      if (quantity <= 0) {
        continue;
      }

      rows.push({
        scheduleItemId: phase.scheduleItemId,
        taskType: phase.taskType,
        label: phase.label,
        quantity,
        actualQuantity: Number(phase.current) || 0,
        totalQuantity: Number(phase.total) || 0,
        estimatedTimeHours: Number(phase.estimatedTimeHours) || 0,
      });
    }

    return rows;
  }

  const total =
    Number(act.apontar.cabo ?? 0) +
    Number(act.apontar.instal ?? 0) +
    Number(act.apontar.confg ?? 0);

  if (total <= 0 || !act.scheduleItemId) {
    return rows;
  }

  const firstProgress = act.progress[0];

  rows.push({
    scheduleItemId: act.scheduleItemId,
    taskType: act.taskType ?? 'activity',
    label: act.title,
    quantity: total,
    actualQuantity: Number(firstProgress?.current ?? 0),
    totalQuantity: Number(firstProgress?.total ?? 0),
    completionPercentage: act.completionPercentage,
  });

  return rows;
}

const QUANTITATIVE_TASK_TYPES = ['cabling', 'installation', 'configuration'];

function inferCompletionOnlyType(act: RdoAtividade): boolean {
  if (act.activityType === 'commissioning') {
    return true;
  }

  if (act.isPendingType) {
    return true;
  }

  const hasQuantPhases = (act.phases ?? []).some(
    (phase) =>
      phase.taskType && QUANTITATIVE_TASK_TYPES.includes(phase.taskType),
  );
  if (hasQuantPhases) {
    return false;
  }

  const hasQuantApontar =
    (act.apontar?.cabo ?? 0) > 0 ||
    (act.apontar?.instal ?? 0) > 0 ||
    (act.apontar?.confg ?? 0) > 0;
  if (hasQuantApontar) {
    return false;
  }

  const hasQuantApontamentos = (act.apontamentos ?? []).some(
    (row) => row.taskType && QUANTITATIVE_TASK_TYPES.includes(row.taskType),
  );

  return !hasQuantApontamentos;
}

export function normalizeLoadedAtividade(raw: RdoAtividade): RdoAtividade {
  const apontamentos = Array.isArray(raw.apontamentos) ? raw.apontamentos : [];
  const fromApontamentos = hydrateApontarFromApontamentos(apontamentos);
  const apontar = mergeApontar(fromApontamentos, raw.apontar);

  const merged: RdoAtividade = {
    ...raw,
    apontar,
    apontamentos,
  };

  return {
    ...merged,
    isPendingType: inferCompletionOnlyType(merged),
  };
}

/**
 * Percentual de uma fase considerando o apontado neste RDO somado ao realizado
 * anterior, limitado a 100%. Espelha taskLiveCompletionPct do composable.
 */
function phaseLiveCompletionPct(
  phase: RdoAtividadePhase,
  apontar: RdoAtividade['apontar'],
): number {
  const key = APONTAR_KEY_BY_TASK[phase.taskType];
  const apontarQty = key ? Number(apontar?.[key]) || 0 : 0;
  const prev = Number(phase.total) || 0;

  if (prev > 0) {
    const real = (Number(phase.current) || 0) + apontarQty;
    return Math.min(100, Math.max(0, (real / prev) * 100));
  }

  return 0;
}

/**
 * Percentual de conclusão da atividade idêntico ao exibido em «Lançar atividade»
 * (liveActivityCompletionPct): média ponderada por horas estimadas das fases
 * (Cabo/Instal/Config), via calculateParentCompletion. Atividades de
 * preenchimento direto (pendência/comissionamento) usam o valor informado.
 */
export function atividadeCompletionPct(act: RdoAtividade): number {
  if (act.isPendingType) {
    return act.completionPercentage != null
      ? Math.min(100, Math.max(0, Math.round(Number(act.completionPercentage))))
      : 0;
  }

  const quantPhases = (act.phases ?? []).filter(
    (phase) =>
      phase.taskType && QUANTITATIVE_TASK_TYPES.includes(phase.taskType),
  );

  if (!quantPhases.length) {
    return act.completionPercentage != null
      ? Math.round(Number(act.completionPercentage))
      : 0;
  }

  const children = quantPhases.map((phase) => ({
    completion_percentage: phaseLiveCompletionPct(phase, act.apontar),
    estimated_time_hours: Number(phase.estimatedTimeHours) || 0,
    total_value: 0,
  }));

  return Math.round(calculateParentCompletion(children));
}

export function serializeAtividadeForPayload(
  act: RdoAtividade,
): Omit<RdoAtividade, 'apontar'> {
  const rest = { ...act };
  delete (rest as Partial<RdoAtividade>).apontar;

  return {
    ...rest,
    apontamentos: buildApontamentosFromAtividade(act),
    // Envia o percentual já calculado (igual ao «Lançar atividade») para que o
    // PDF/web exibam o mesmo número, sem recalcular de forma divergente.
    completionPercentage: act.isPendingType
      ? act.completionPercentage
      : atividadeCompletionPct(act),
  };
}
